import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Form, Button, Alert } from 'antd';
import { Input } from 'components/Form';
import { EnvironmentType } from '../../types';
import Spinner from 'components/Spinner';
import DefaultLayout from 'components/DefaultLayout';
import useQueryApiClient from 'utils/useApiClient';

interface EmailSettingsTabProps {
  type: EnvironmentType;
  selectedResellerId: number;
  selectedCustomerId: number;
}

const EmailSettingsForm: React.FC<EmailSettingsTabProps> = ({
  type,
  selectedResellerId,
  selectedCustomerId,
}) => {
  const [emailSettings, setEmailSettings] = useState<any>(null);

  const intl = useIntl();
  const [form] = Form.useForm();

  const ownerId = selectedCustomerId ? selectedCustomerId : selectedResellerId;

  const {
    data: emailSettingsData,
    fetch: fetchEmailSettings,
    isLoading: fetchLoading,
  } = useQueryApiClient({
    request: {
      disableOnMount: !ownerId && type !== 'GLOBAL',
      url: `/api/v1/customize-environment/email-settings/${type}/${ownerId}`,
      method: 'GET',
    },
  });

  const {
    appendData: updateEmailSettings,
    isLoading: updateLoading,
  } = useQueryApiClient({
    request: {
      disableOnMount: true,
      url: `/api/v1/customize-environment/email-settings/:id`,
      method: 'PUT',
    },
  });

  const {
    appendData: createEmailSettings,
    isLoading: createLoading,
  } = useQueryApiClient({
    request: {
      disableOnMount: true,
      url: `/api/v1/customize-environment/email-settings`,
      method: 'POST',
    },
  });

  const loading = fetchLoading || updateLoading || createLoading;

  useEffect(() => {
    if (!(ownerId || type === 'GLOBAL')) {
       return;
    }

    fetchEmailSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, selectedResellerId, selectedCustomerId]);

  useEffect(() => {
    if (emailSettingsData?.emailSettings) {
      setEmailSettings(emailSettingsData.emailSettings);
      form.setFieldsValue(emailSettingsData.emailSettings);
    }
  }, [emailSettingsData, form]);

  const submitForm = async (values: any) => {
    let parsedValues = {
      ...values,
      ownerType: type,
      ownerId,
    };

    if (emailSettings?.id) {
      await updateEmailSettings(parsedValues, { id: emailSettings.id });
    } else {
      await createEmailSettings(parsedValues);
    }
  };

  return (
    <>
      <Spinner spinning={loading}>
        {
          selectedCustomerId || selectedResellerId || type === 'GLOBAL' ? 
            <>
              <Form form={form} onFinish={submitForm}>
                <Input name="emailName" label={intl.formatMessage({id: 'system.environment.email_name'})} validation={{required: true}} />
                <Input name="from" label={intl.formatMessage({id: 'system.environment.standard_sending_adress'})}/>
                <Input name="mailDriver" label={intl.formatMessage({id: 'system.environment.mail_driver'})}/>
                <Input name="mailHost" label={intl.formatMessage({id: 'system.environment.mail_host'})}/>
                <Input name="mailPort" label={intl.formatMessage({id: 'system.environment.mail_port'})}/>
                <Input name="mailEncryption" label={intl.formatMessage({id: 'system.environment.mail_encryption'})}/>
                <Input name="mailUsername" label={intl.formatMessage({id: 'system.environment.mail_username'})}/>
                <Input name="mailPassword" label={intl.formatMessage({id: 'system.environment.mail_password'})}/>
              </Form>
              <DefaultLayout.PageFooter
                right={
                  <Button
                    type="primary"
                    onClick={form.submit}
                  >
                    <span><FormattedMessage id="general.save" /></span>
                  </Button>
                }
              />
            </>
          :
            <Alert
              message={intl.formatMessage({ id: `system.environment.please_select_a_${type === 'RESELLER' ? 'reseller' : 'customer'}` })}
              type="info"
              showIcon
            />
        }
      </Spinner>

    </>
  );
}

export default EmailSettingsForm;
